import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Wrench,
  Plus,
  Search,
  Send,
  Pause,
  Play,
  Archive,
  X,
  Calendar,
  DollarSign,
  User,
  Clock,
  CheckCircle2,
  RefreshCw,
  Link2,
  Copy,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { Drawer, Modal } from '@/components/ui/drawer';
import { Input, Select } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { PageHeader } from '@/components/ui/page-header';
import { api, formatCurrency, formatDate, cn } from '@/lib/utils';
import { PAYMENT_METHODS, addMonths, STRIPE_CONNECT_ENABLED } from '@maintenance-tracker/shared';
import { PlanNameInput } from '@/components/plan-name-input';
import { CsvImportModal } from '@/components/csv-import-modal';
import { useImportModalFromSearch } from '@/lib/import-search';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paused', label: 'Paused' },
  { key: 'all', label: 'All' },
] as const;

export function AgreementsPage() {
  const search = useSearch({ strict: false }) as { tab?: string; selected?: string; q?: string };
  const [tab, setTab] = useState(search.tab ?? 'active');
  const [q, setQ] = useState(search.q ?? '');
  const [selected, setSelected] = useState<string | null>(search.selected ?? null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  useImportModalFromSearch(setImportOpen);
  const qc = useQueryClient();

  useEffect(() => {
    if (search.tab && TABS.some((t) => t.key === search.tab)) {
      setTab(search.tab as (typeof TABS)[number]['key']);
    }
  }, [search.tab]);

  useEffect(() => {
    if (search.q != null) setQ(search.q);
  }, [search.q]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agreements', tab, q],
    queryFn: () => api<{ items: AgreementRow[] }>(`/api/v1/obligations?tab=${tab}&search=${encodeURIComponent(q)}`),
  });

  const detail = useQuery({
    queryKey: ['agreement', selected],
    queryFn: () => api<AgreementDetail>(`/api/v1/obligations/${selected}`),
    enabled: !!selected,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['agreements'] });
    qc.invalidateQueries({ queryKey: ['agreement', selected] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const sendReminder = useMutation({
    mutationFn: (id: string) => api(`/api/v1/obligations/${id}/send-reminder`, { method: 'POST', body: '{}' }),
    onSuccess: invalidate,
  });
  const pause = useMutation({
    mutationFn: (id: string) => api(`/api/v1/obligations/${id}/pause`, { method: 'POST', body: JSON.stringify({ reason: 'other' }) }),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: (id: string) => api(`/api/v1/obligations/${id}/resume`, { method: 'POST', body: '{}' }),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/v1/obligations/${id}/archive`, { method: 'POST', body: '{}' }),
    onSuccess: () => { setSelected(null); invalidate(); },
  });

  const generateLink = useMutation({
    mutationFn: (id: string) => api<{ url: string }>(`/api/v1/obligations/${id}/payment-link`, { method: 'POST', body: '{}' }),
    onSuccess: () => invalidate(),
  });

  const updatePaymentSettings = useMutation({
    mutationFn: (payload: { payment_link_enabled?: boolean; auto_renew?: boolean; payment_link_url?: string }) =>
      api(`/api/v1/obligations/${selected}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: invalidate,
  });

  const [customPayUrl, setCustomPayUrl] = useState('');
  useEffect(() => {
    const raw = (detail.data as AgreementDetail & { paymentLinkUrl?: string | null })?.paymentLinkUrl;
    setCustomPayUrl(raw ?? '');
  }, [detail.data?.id, (detail.data as AgreementDetail & { paymentLinkUrl?: string | null })?.paymentLinkUrl]);

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState({
    new_end_date: '',
    payment_method: 'stripe' as (typeof PAYMENT_METHODS)[number],
    amount_received: 0,
    note: '',
  });

  const openRenew = () => {
    const d = detail.data;
    if (!d) return;
    const months = d.metadata?.interval_months ?? 12;
    const base = d.endDate && d.endDate >= new Date().toISOString().slice(0, 10)
      ? d.endDate
      : new Date().toISOString().slice(0, 10);
    setRenewForm({
      new_end_date: addMonths(base, months),
      payment_method: 'stripe',
      amount_received: d.amount ?? 0,
      note: '',
    });
    setRenewOpen(true);
  };

  const renew = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/obligations/${id}/renew`, {
        method: 'POST',
        body: JSON.stringify({
          new_end_date: renewForm.new_end_date,
          payment_method: renewForm.payment_method,
          amount_received: renewForm.amount_received || undefined,
          note: renewForm.note || undefined,
        }),
      }),
    onSuccess: () => { setRenewOpen(false); invalidate(); },
  });
  const bulk = useMutation({
    mutationFn: (action: string) =>
      api('/api/v1/obligations/bulk', {
        method: 'POST',
        body: JSON.stringify({ obligation_ids: [...selectedIds], action }),
      }),
    onSuccess: () => { setSelectedIds(new Set()); invalidate(); },
  });

  const rows = data?.items ?? [];
  const allChecked = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Maintenance agreements"
        description="Track renewals, send reminders, and keep every plan active."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Import from spreadsheet
            </Button>
            <Link to="/agreements/new">
              <Button>
                <Plus className="h-4 w-4" /> Add agreement
              </Button>
            </Link>
          </div>
        }
      />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['agreements'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
          qc.invalidateQueries({ queryKey: ['risk'] });
        }}
        defaultKind="agreements"
        allowedKinds={['agreements', 'customers', 'services', 'equipment']}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex w-full gap-0.5 overflow-x-auto rounded-lg border border-border bg-bg-subtle/70 p-1 sm:w-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'shrink-0 rounded-md px-3 py-1.5 text-[13px] font-medium transition-all',
                tab === t.key
                  ? 'bg-bg-surface text-text-primary shadow-xs'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="sm:w-72">
          <Input
            placeholder="Search customer, plan, or ref…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            leadingIcon={<Search />}
          />
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand-border bg-brand-subtle px-4 py-2.5 shadow-xs animate-slide-up">
          <span className="text-sm font-medium text-brand-active">{selectedIds.size} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" loading={bulk.isPending} onClick={() => bulk.mutate('send_reminder')}>
              <Send className="h-3.5 w-3.5" /> Send reminder
            </Button>
            <Button variant="secondary" size="sm" onClick={() => bulk.mutate('pause')}>
              <Pause className="h-3.5 w-3.5" /> Pause
            </Button>
            <Button variant="ghost" size="sm" onClick={() => bulk.mutate('archive')}>
              <Archive className="h-3.5 w-3.5" /> Archive
            </Button>
            <button onClick={() => setSelectedIds(new Set())} className="rounded-lg p-1.5 text-text-muted hover:bg-bg-surface" aria-label="Clear selection">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between rounded-xl border border-danger/25 bg-danger-subtle px-4 py-3 text-sm text-danger">
          <span>Failed to load agreements.</span>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      <Card noPadding>
        <div className="scroll-slim overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-muted text-left text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    className="h-4 w-4 cursor-pointer rounded border-border-strong accent-brand"
                    checked={allChecked}
                    onChange={(e) => {
                      setSelectedIds(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set());
                    }}
                  />
                </th>
                <th className="px-2 py-3">Customer</th>
                <th className="px-2 py-3">Plan</th>
                <th className="px-2 py-3 text-right">Amount</th>
                <th className="px-2 py-3">Renews</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-4" /></td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2.5">
                        <Skeleton className="h-9 w-9 rounded-full" />
                        <Skeleton className="h-4 w-28" />
                      </div>
                    </td>
                    <td className="px-2 py-3"><Skeleton className="h-4 w-32" /></td>
                    <td className="px-2 py-3"><Skeleton className="ml-auto h-4 w-16" /></td>
                    <td className="px-2 py-3"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-2 py-3"><Skeleton className="h-6 w-20 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="ml-auto h-8 w-20" /></td>
                  </tr>
                ))
              ) : rows.length ? (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-bg-subtle/60"
                    onClick={() => setSelected(row.id)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.contactName}`}
                        className="h-4 w-4 cursor-pointer rounded border-border-strong accent-brand"
                        checked={selectedIds.has(row.id)}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(row.id);
                          else next.delete(row.id);
                          setSelectedIds(next);
                        }}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={row.contactName} size="md" />
                        <span className="font-medium text-text-primary">{row.contactName}</span>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-text-secondary">{row.planName}</td>
                    <td className="px-2 py-3 text-right font-medium tabular-nums">{formatCurrency(row.amount ?? 0)}</td>
                    <td className="px-2 py-3">
                      <div className="whitespace-nowrap">
                        <span className="text-text-primary">{row.endDate ? formatDate(row.endDate) : '—'}</span>
                        {row.relativeEndDate && (
                          <span className="ml-1.5 text-xs text-text-muted">· {row.relativeEndDate}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3"><StatusBadge status={row.displayStatus} /></td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={sendReminder.isPending && sendReminder.variables === row.id}
                        onClick={() => sendReminder.mutate(row.id)}
                      >
                        <Send className="h-3.5 w-3.5" /> Remind
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Wrench}
                      title="No maintenance agreements yet"
                      description="Add your first customer plan to start tracking renewals and reminders."
                      action={
                        <Link to="/agreements/new">
                          <Button>
                            <Plus className="h-4 w-4" /> Add agreement
                          </Button>
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={detail.data?.title ?? 'Agreement'}
        subtitle={
          detail.data ? (
            <div className="flex items-center gap-2">
              <StatusBadge status={detail.data.status === 'paused' ? 'paused' : 'active'} />
            </div>
          ) : undefined
        }
        footer={
          detail.data ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={openRenew}>
                <RefreshCw className="h-3.5 w-3.5" /> Mark renewed
              </Button>
              <Button variant="secondary" size="sm" onClick={() => sendReminder.mutate(detail.data!.id)} loading={sendReminder.isPending}>
                <Send className="h-3.5 w-3.5" /> Send reminder
              </Button>
              {detail.data.status === 'paused' ? (
                <Button variant="secondary" size="sm" onClick={() => resume.mutate(detail.data!.id)} loading={resume.isPending}>
                  <Play className="h-3.5 w-3.5" /> Resume
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => pause.mutate(detail.data!.id)} loading={pause.isPending}>
                  <Pause className="h-3.5 w-3.5" /> Pause
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => archive.mutate(detail.data!.id)} loading={archive.isPending}>
                <Archive className="h-3.5 w-3.5" /> Archive
              </Button>
            </div>
          ) : undefined
        }
      >
        {detail.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-40 rounded-xl" />
          </div>
        ) : detail.data ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-bg-muted p-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-3.5">
                <DetailRow icon={User} label="Customer" value={detail.data.contact?.name ?? '—'} />
                <DetailRow icon={DollarSign} label="Amount" value={formatCurrency(detail.data.amount ?? 0)} />
                <DetailRow icon={Calendar} label="Renews on" value={detail.data.endDate ? formatDate(detail.data.endDate) : '—'} />
              </dl>
            </div>

            <div>
              <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-text-secondary">Payment link</h3>
              <div className="space-y-3 rounded-xl border border-border bg-bg-muted p-4">
                {detail.data.payment_url ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="flex-1 truncate rounded bg-bg-surface px-2 py-1 text-xs text-text-secondary">
                      {detail.data.payment_url}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigator.clipboard?.writeText(detail.data!.payment_url!)}
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary">
                    Add a renewal page link under{' '}
                    <Link to="/settings/payments" className="font-medium text-brand hover:underline">
                      Settings → Renewal page links
                    </Link>{' '}
                    so renewal emails can include a pay button.
                  </p>
                )}
                <Input
                  label="Custom link for this agreement (optional)"
                  placeholder="Leave blank to use your shop default from Settings"
                  value={customPayUrl}
                  onChange={(e) => setCustomPayUrl(e.target.value)}
                  onBlur={() => {
                    const raw = (detail.data as AgreementDetail & { paymentLinkUrl?: string | null })?.paymentLinkUrl ?? '';
                    if (customPayUrl.trim() !== raw.trim()) {
                      updatePaymentSettings.mutate({ payment_link_url: customPayUrl.trim() || '' });
                    }
                  }}
                />
                {STRIPE_CONNECT_ENABLED && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={generateLink.isPending}
                      onClick={() => generateLink.mutate(detail.data!.id)}
                    >
                      <Link2 className="h-3.5 w-3.5" /> {detail.data.payment_url ? 'Refresh hosted link' : 'Generate hosted link'}
                    </Button>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={detail.data.metadata?.payment_link_enabled !== false}
                    onChange={(e) =>
                      updatePaymentSettings.mutate({ payment_link_enabled: e.target.checked })
                    }
                  />
                  Include renewal page link in emails
                </label>
                {STRIPE_CONNECT_ENABLED && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!detail.data.metadata?.auto_renew}
                      onChange={(e) => updatePaymentSettings.mutate({ auto_renew: e.target.checked })}
                    />
                    Auto-renew on stored card (Growth/Pro)
                  </label>
                )}
                {(detail.data.payments ?? []).length > 0 && (
                  <div className="border-t border-border pt-3">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">Payment history</p>
                    <ul className="space-y-2">
                      {(detail.data.payments ?? []).map((p) => (
                        <li key={p.id} className="flex justify-between text-sm">
                          <span>{formatCurrency(p.amount)}</span>
                          <span className="text-text-muted">
                            {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : p.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-text-secondary">Activity</h3>
              {(detail.data.timeline ?? []).length ? (
                <ol className="relative space-y-4 border-l border-border pl-5">
                  {(detail.data.timeline ?? []).slice(0, 12).map((e, i) => {
                    const isReminder = 'templateKey' in e && e.templateKey;
                    const when = ('sentAt' in e && e.sentAt) ? e.sentAt : (e.createdAt ?? '');
                    return (
                      <li key={i} className="relative">
                        <span className="absolute -left-[26px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-bg-surface ring-1 ring-border">
                          {isReminder ? (
                            <Send className="h-2.5 w-2.5 text-brand" />
                          ) : (
                            <CheckCircle2 className="h-2.5 w-2.5 text-success" />
                          )}
                        </span>
                        <p className="text-sm capitalize text-text-primary">
                          {isReminder ? (e as { templateKey: string }).templateKey.replace(/_/g, ' ') : (e as { action: string }).action?.replace(/_/g, ' ')}
                        </p>
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-text-muted">
                          <Clock className="h-3 w-3" />
                          {when ? new Date(when).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="text-sm text-text-muted">No activity recorded yet.</p>
              )}
            </div>
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={renewOpen}
        onClose={() => setRenewOpen(false)}
        title="Mark as renewed"
        description="Record the renewal and we'll reschedule reminders for the new term."
      >
        <div className="space-y-4">
          <Input
            label="New renewal date"
            type="date"
            value={renewForm.new_end_date}
            onChange={(e) => setRenewForm({ ...renewForm, new_end_date: e.target.value })}
            required
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Payment method"
              value={renewForm.payment_method}
              onChange={(e) =>
                setRenewForm({ ...renewForm, payment_method: e.target.value as (typeof PAYMENT_METHODS)[number] })
              }
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m === 'stripe' ? 'Card (Stripe)' : m.charAt(0).toUpperCase() + m.slice(1)}
                </option>
              ))}
            </Select>
            <Input
              label="Amount received"
              type="number"
              prefix="$"
              value={renewForm.amount_received || ''}
              onChange={(e) => setRenewForm({ ...renewForm, amount_received: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <Input
            label="Note (optional)"
            placeholder="e.g. Renewed over the phone"
            value={renewForm.note}
            onChange={(e) => setRenewForm({ ...renewForm, note: e.target.value })}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setRenewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selected && renew.mutate(selected)}
              loading={renew.isPending}
              disabled={!renewForm.new_end_date}
            >
              Confirm renewal
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="flex items-center gap-2 text-[13px] text-text-secondary">
        <Icon className="h-4 w-4 text-text-muted" />
        {label}
      </dt>
      <dd className="text-sm font-medium text-text-primary">{value}</dd>
    </div>
  );
}

interface AgreementRow {
  id: string;
  contactName: string;
  planName: string;
  amount: number;
  endDate: string;
  relativeEndDate: string;
  displayStatus: string;
}

interface AgreementDetail {
  id: string;
  title: string;
  amount: number;
  endDate: string;
  status: string;
  contact: { name: string };
  payment_url?: string | null;
  metadata?: { interval_months?: number; plan_name?: string; payment_link_enabled?: boolean; auto_renew?: boolean };
  payments?: Array<{ id: string; amount: number; status: string; paidAt?: string | null }>;
  timeline: Array<{ sentAt?: string; createdAt?: string; templateKey?: string; action?: string }>;
}

export function NewAgreementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    plan_name: '',
    amount: 0,
    currency: 'USD' as const,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    interval_months: 12,
  });

  const submit = async () => {
    setLoading(true);
    try {
      await api('/api/v1/obligations', { method: 'POST', body: JSON.stringify(form) });
      navigate({ to: '/agreements' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <PageHeader title="Add maintenance agreement" description="Create a plan and we'll schedule renewal reminders automatically." />
      <Card>
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required />
            <Input label="Customer email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
          </div>
          <PlanNameInput value={form.plan_name} onChange={(plan_name) => setForm({ ...form, plan_name })} required />
          <Input
            label="Amount"
            type="number"
            prefix="$"
            value={form.amount || ''}
            onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <Input label="End date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2 border-t border-border pt-5">
          <Button variant="secondary" onClick={() => navigate({ to: '/agreements' })}>Cancel</Button>
          <Button onClick={submit} loading={loading}>Save agreement</Button>
        </div>
      </Card>
    </div>
  );
}
